import {
  Body,
  Button,
  Flex,
  Glide,
  LazyImage,
  Shape,
  SubTitle,
  Title,
  styled,
} from '@teko/ds-desktop';
import { formatCurrency } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { useCartV2, useProductDetailState, useTenantConfig } from 'contexts';
import { useProductCombos } from 'hooks';
import { t } from 'i18n';
import React, { useEffect, useState } from 'react';
import { IActiveCombo, ISelectingProduct } from 'types';
import ComboPromotion from '../../../../../assets/combo-promotion-v2.svg';
import { ComboItem } from './ComboItem';
import { ComboSelectionModal } from './ComboSelectionModal';

interface IProductSelectionModalProps {
  selectingProduct?: Pick<ISelectingProduct, 'conditionKey'> & {
    sku: string;
  };
}

export const ComboCarousel: React.FC = () => {
  const { numberDisplayFormat } = useTenantConfig();
  const { onAddComboItems } = useCartV2();
  const { product } = useProductDetailState();
  const productComboHook = useProductCombos({
    skus: product?.sku ? [product.sku] : undefined,
  });
  const { activeCombo, setActiveCombo, comboGroup, productCombos } =
    productComboHook;
  // activeTab state includes index of currently active tab and total products of each combo in active group
  const [activeTab, setActiveTab] = useState<{
    index: number;
    totalProductTab?: number;
  }>({
    index: 0,
  });
  // Index of selected slider in Glide on each tab
  const [comboIndex, setComboIndex] = useState(0);
  // State to keep product sku that user click to "Change product", state also keep both combo key & condition key containing product
  const [selectingProduct, setSelectingProduct] =
    useState<IProductSelectionModalProps['selectingProduct']>();

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // set initial activeTab value
    if (comboGroup && !activeTab.totalProductTab) {
      setActiveTab({
        index: 0,
        totalProductTab: Number(Object.keys(comboGroup)[0]),
      });
    }
  }, [JSON.stringify(comboGroup)]);

  return !!productCombos.length &&
    !!comboGroup &&
    !!activeTab.totalProductTab ? (
    <>
      <Shape backgroundColor="white" borderRadius="xl" marginBottom="s16">
        <Shape
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FFF7E6',
            borderRadius: '16px 16px 0 0',
            marginBottom: '16px',
          }}
        >
          <LazyImage src={ComboPromotion} height={60} width={132} />
          <Title style={{ display: 'inline', marginLeft: '8px' }}>
            {t('Mua kèm nhận ưu đãi')}
          </Title>
        </Shape>

        <Flex direction="column" gap="s16">
          <Flex padding={['s0', 's16']} gap="s16">
            {Object.entries(comboGroup).map(
              ([totalProduct]: [string, { combos: IActiveCombo[] }], index) => {
                const isActiveTab = index === activeTab.index;
                return (
                  <ComboTabWrapper
                    key={`${index}${totalProduct}`}
                    padding="s8"
                    border={
                      isActiveTab ? '1px solid primary' : '1px solid divider'
                    }
                    borderRadius="xl"
                    onClick={() => {
                      setActiveTab({
                        index,
                        totalProductTab: Number(totalProduct),
                      });
                      setComboIndex(0);
                    }}
                    {...getTrackingData({
                      isTrackContent: true,
                      regionName: 'productComboList',
                      contentName: 'btnChangeComboTab',
                      payload: JSON.stringify({
                        comboTab: `Combo ${totalProduct} sản phẩm`,
                        comboIndex: index + 1,
                      }),
                    })}
                  >
                    <Body
                      level={isActiveTab ? 2 : 1}
                      color={isActiveTab ? 'primary' : ''}
                    >
                      {t('Combo {{totalProduct}} sản phẩm', {
                        totalProduct,
                      })}
                    </Body>
                  </ComboTabWrapper>
                );
              },
            )}
          </Flex>
          <GlideWrapper width="100%">
            <Glide
              perView={1}
              startAt={comboIndex}
              onSlideChange={(index: number) => setComboIndex(index)}
              key={visible ? 1 : 0}
              displayNewLayout={true}
              trackingArrowRight={getTrackingData({
                isTrackContent: true,
                regionName: 'productComboList',
                contentName: 'btnChangeCombo',
                payload: JSON.stringify({
                  comboTab: `Combo ${activeTab.totalProductTab} sản phẩm`,
                  comboPromotion:
                    comboGroup[activeTab.totalProductTab].combos[comboIndex]
                      .promotion.id,
                  comboIndex: comboIndex + 1,
                  target: 'next',
                }),
              })}
              trackingArrowLeft={getTrackingData({
                isTrackContent: true,
                regionName: 'productComboList',
                contentName: 'btnChangeCombo',
                payload: JSON.stringify({
                  comboTab: `Combo ${activeTab.totalProductTab} sản phẩm`,
                  comboPromotion:
                    comboGroup[activeTab.totalProductTab].combos[comboIndex]
                      .promotion.id,
                  comboIndex: comboIndex + 1,
                  target: 'previous',
                }),
              })}
            >
              {comboGroup[activeTab.totalProductTab].combos.map(
                (combo, comboIndex) => {
                  const {
                    promotion,
                    conditions,
                    totalAmount,
                    totalDiscount,
                    totalProduct,
                    key,
                  } = combo;
                  const addComboToCart = async () => {
                    const mainPayload = {
                      override: true,
                      groups: [
                        {
                          quantity: 1,
                          promotionId: promotion.id,
                          products: conditions.map((el, index) => ({
                            sku: el.product.sku,
                            promotionId: !!el.product.bestPromotionIds?.length
                              ? el.product.bestPromotionIds?.[0]
                              : null,
                            quantity: el.requiredQuantity,
                            isPrimary: index === 0,
                          })),
                        },
                      ],
                    };
                    await onAddComboItems(mainPayload, false);
                  };
                  return (
                    <React.Fragment key={`${comboIndex}${key}`}>
                      <ComboItem
                        combo={combo}
                        onClickProductChange={(
                          selectedSku: string,
                          conditionKey: number,
                        ) => {
                          setVisible(true);
                          setSelectingProduct({
                            sku: selectedSku,
                            conditionKey,
                          });
                          setActiveCombo(combo);
                        }}
                      />
                      <Flex
                        align="center"
                        justify="space-between"
                        borderTop="1px solid divider"
                        marginTop="s16"
                        padding="s16"
                      >
                        <Flex direction="column" gap="s4">
                          <Flex gap="s8">
                            <Body level={1}>{`${t('Tổng tiền')}:`}</Body>
                            <SubTitle color="primary" level={1}>
                              {formatCurrency(totalAmount, numberDisplayFormat)}
                            </SubTitle>
                          </Flex>
                          <Flex gap="s8">
                            <Body color="primary" level={1}>{`${t(
                              'Tiết kiệm',
                            )}:`}</Body>
                            <Body color="primary" level={1}>
                              {formatCurrency(
                                totalDiscount,
                                numberDisplayFormat,
                              )}
                            </Body>
                          </Flex>
                        </Flex>
                        <Shape>
                          <Button
                            variant="secondary"
                            onClick={addComboToCart}
                            {...getTrackingData({
                              isTrackContent: true,
                              regionName: 'productComboList',
                              contentName: 'btnAddToCart',
                              payload: JSON.stringify({
                                promotionType: 'combo',
                                comboTab: `Combo ${totalProduct} sản phẩm`,
                                comboPromotion: promotion.id,
                                comboIndex: comboIndex + 1,
                                skus: conditions.map((con) => con.product.sku),
                              }),
                            })}
                          >
                            <SubTitle level={2} color="primary">
                              {t('Thêm vào giỏ hàng')}
                            </SubTitle>
                          </Button>
                        </Shape>
                      </Flex>
                    </React.Fragment>
                  );
                },
              )}
            </Glide>
          </GlideWrapper>
        </Flex>
      </Shape>
      {visible && !!activeCombo && (
        <ComboSelectionModal
          closeModal={(selectingCombo?: IActiveCombo) => {
            setVisible(false);
            setSelectingProduct(undefined);
            // set active tab according to active combo
            if (selectingCombo) {
              const indexTab = Object.keys(comboGroup).findIndex(
                (item) => Number(item) === selectingCombo.totalProduct,
              );
              setActiveTab({
                index: indexTab,
                totalProductTab: selectingCombo.totalProduct,
              });
              if (comboGroup && activeTab.totalProductTab) {
                const activeGroup = comboGroup[activeTab.totalProductTab];
                const selectedIndex = activeGroup.combos.findIndex(
                  (el) => el.key === selectingCombo.key,
                );
                setComboIndex(selectedIndex);
              }
            }
          }}
          selectingProduct={selectingProduct}
          visible={visible}
          {...productComboHook}
        />
      )}
    </>
  ) : null;
};

const ComboTabWrapper = styled(Shape)`
  border-radius: 999px;
  cursor: pointer;
`;

const GlideWrapper = styled(Shape)`
  .glide__track,
  .glide__slides {
    overflow-y: unset !important;
    overflow-x: clip !important;
  }
  .glide__arrow--left {
    top: 60px !important;
    left: 16px !important;
  }
  .glide__arrow--right {
    top: 40px !important;
    right: 16px !important;
  }
`;
